<template>
  <div v-if="globalStore.isBot && !isShowChart">
    <h2 class="header heading-m">История цены</h2>
    <div class="isBot__body-chart"></div>
    <div v-if="isCollapsed" class="price-chart-footer">
      <button
        class="price-chart__btn price-chart__btn--wide body-m-regular"
        @click="emit('to-route', 'price-chart')"
      >
        Подробнее
        <img :src="`/static/images/arrow-button-right.svg`" alt="arrow" />
      </button>
    </div>
  </div>
  <div
    v-if="isShowChart || isForceShow"
    :class="{ 'price-chart--collapsed': isCollapsed }"
  >
    <h2 class="header heading-m">История цены</h2>
    <div v-if="!isCollapsed" class="price-chart-wrapper__btn">
      <button
        v-if="isShowChartDaily"
        :class="[
          'price-chart__btn',
          { 'price-chart__btn_active': showTime === 'daily' },
          'body-m-regular',
        ]"
        @click="setTimePeriod('daily')"
      >
        За месяц
      </button>
      <button
        v-if="
          isShowChartSixMonth && axiosData?.sixMonth?.categories?.length > 5
        "
        :class="[
          'price-chart__btn price-chart__btn_margin',
          { 'price-chart__btn_active': showTime === 'sixMonth' },
          'body-m-regular',
        ]"
        @click="setTimePeriod('sixMonth')"
      >
        За полгода
      </button>
    </div>
    <div class="chart-position">
      <highchart :key="showTime" :options="chartOptions" />
    </div>
    <div v-if="isCollapsed" class="price-chart-footer">
      <ui-ui-button
        type="neutral"
        :resize="true"
        @click="emit('to-route', 'price-chart')"
      >
        Подробнее
      </ui-ui-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn, useWindowSize } from '@vueuse/core';
import { API } from '~/api/api';
import { useGlobalStore } from '~/stores/global';
import { useMediaQueryStore } from '~/stores/mediaQuery';

interface Item {
  name?: string;
  daily?: {
    categories: string[];
    series: string[];
  };
  sixMonth?: {
    categories: string[];
    series: string[];
  };
}

interface chartOptionsInterface {
  deep: boolean;
  accessibility: object;
  credits: object;
  chart: any;
  title: any;
  subtitle: any;
  plotOptions: any;
  xAxis: {
    tickInterval?: number;
    lineColor: string;
    minorGridLineWidth: number;
    tickWidth: number;
    gridLineWidth: number;
    crosshair: object | boolean;
    lineWidth: number;
    gridLineColor: string;
    gridLineDashStyle: string;
    showFirstLabel?: boolean;
    showLastLabel?: boolean;
    rotation?: number;
    visible?: boolean;
    labels: {
      formatter?: (value: string) => {} | undefined;
      rotation?: number;
      align: string;
      reserveSpace: boolean;
      x: number;
      y: number;
      style: {
        fontSize: string;
        color: string;
        fontWeight: string;
        lineHeight: string;
      };
    };
    categories: string[];
    min?: number;
    max?: number;
  };
  yAxis: {
    visible?: boolean;
    crosshair: boolean;
    gridLineColor: string;
    gridLineDashStyle: string;
    alignTicks: boolean;
    title: {
      text: string;
    };
    labels: {
      formatter: () => {};
      reserveSpace: boolean;
      padding: number;
      x: number;
      y: number;
      align: string;
      style: {
        color: string;
        fontWeight: string;
        fontSize: string;
        lineHeight: string;
        verticalAlign: string;
      };
    };
    lineColor?: string;
    minorGridLineWidth?: number;
    tickWidth?: number;
    gridLineWidth?: number;
    lineWidth?: number;
    categories?: string[];
    min?: number;
    max?: number;
  };
  tooltip: {
    outside?: boolean;
    valueSuffix: string;
    useHTML: boolean;
    formatter: () => {};
    shared: boolean;
    backgroundColor: string;
    borderColor: string;
    borderRadius: number;
    borderWidth: number;
    followPointer?: boolean;
    style: {
      fontWeight: string;
      fontSize: string;
      lineHeight: string;
      whiteSpace: string;
    };
  };
  legend: {
    useHTML?: boolean;
    followPointer?: boolean;
    labelFormatter?: () => {} | undefined;
    align: string;
    itemMarginTop: number;
    navigation: {
      activeColor: string;
      animation: true;
      arrowSize: number;
      enabled: true;
      inactiveColor: string;
    };
    itemStyle: {
      fontWeight: string;
      fontSize: string;
      lineHeight: string;
    };
    symbolHeight: number;
    symbolWidth: number;
    symbolPadding: number;
    itemDistance: number;
  };
  series: Item[];
}

const props = defineProps({
  pageData: {
    type: Object,
    required: true,
  },
  isForceShow: {
    type: Boolean,
    default: false,
  },
  isCollapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['componentHide', 'to-route', 'change-data']);
const { width } = useWindowSize();
const axiosData = ref(props?.pageData?.priceChart);
const globalStore = useGlobalStore();
const mediaStore = useMediaQueryStore();
const isLg = computed(() => mediaStore.isLg);
const isMobile = computed(
  () =>
    mediaStore.isMobile || mediaStore.isEs || mediaStore.isSm || mediaStore.isMd
);

const showTime = ref<string>(
  props.isCollapsed &&
    axiosData?.value?.sixMonth &&
    axiosData?.value?.sixMonth?.categories?.length > 5
    ? 'sixMonth'
    : 'daily'
);
const reDrawChart = ref(false);

const isShowChart = computed(() => {
  return (
    axiosData?.value &&
    Object.values(axiosData?.value).length > 1 &&
    reDrawChart.value
  );
});

const isShowChartDaily = computed(() => {
  return (
    axiosData?.value &&
    axiosData?.value?.daily &&
    Object.values(axiosData?.value?.daily).length > 1
  );
});

const isShowChartSixMonth = computed(() => {
  return (
    axiosData?.value &&
    axiosData?.value?.sixMonth &&
    Object.values(axiosData?.value?.sixMonth).length > 1
  );
});

const isShowCollapsedSixMonth = computed(() => {
  if (
    axiosData?.value?.sixMonth?.categories &&
    props.isCollapsed &&
    showTime.value === 'sixMonth'
  ) {
    const monthes = [
      ...new Set(
        axiosData?.value?.sixMonth?.categories.map((v: string) =>
          v.replace(/\d+\./g, '')
        )
      ),
    ];
    return monthes.length;
  }
  return false;
});

const chartOptions: chartOptionsInterface = {
  deep: true,
  accessibility: {
    enabled: false,
  },
  credits: {
    enabled: false,
  },
  chart: {
    scrollablePlotArea: {
      minWidth: 1,
    },
    style: {
      fontFamily: 'Inter',
      fontSize: '14px',
    },
  },
  title: {
    text: '',
  },
  subtitle: {
    text: '',
  },
  plotOptions: {
    series: {
      marker: {
        enabled: true,
        symbol: 'circle',
        radius: 1,
        states: {
          hover: {
            radius: 1,
          },
        },
      },
      pointPadding: 0,
      groupPadding: 0,
      animation: false,
    },
  },
  xAxis: {
    lineColor: 'transparent',
    lineWidth: 0,
    minorGridLineWidth: 0,
    tickWidth: 0,
    gridLineWidth: 0,
    gridLineColor: '#D3D3D3',
    gridLineDashStyle: 'Dash',
    crosshair: {
      width: 1,
      color: 'blue',
    },
    labels: {
      align: 'center',
      reserveSpace: false,
      x: 0,
      y: 22,
      style: {
        color: '#86888F',
        fontWeight: '450',
        fontSize: '14px',
        lineHeight: '20px',
      },
    },
    categories: [],
  },
  yAxis: {
    crosshair: false,
    gridLineColor: '#D3D3D3',
    gridLineDashStyle: 'Dash',
    alignTicks: true,
    title: {
      text: '',
    },
    labels: {
      formatter() {
        return isMobile.value
          ? // @ts-ignore
            `${this.value / 1000} K`
          : // @ts-ignore
            `${this.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₽`;
      },
      reserveSpace: true,
      padding: 0,
      x: -5,
      y: 4,
      align: 'right',
      style: {
        color: '#86888F',
        fontWeight: '450',
        fontSize: '14px',
        lineHeight: '20px',
        verticalAlign: 'middle',
      },
    },
  },
  tooltip: {
    valueSuffix: ' ₽',
    useHTML: true,
    outside: false,
    formatter() {
      // @ts-ignore
      const allData = this.points.reduce(function (s, point) {
        return (
          s +
          `<div style="display: flex; align-items: baseline; margin-bottom: 8px"><div style="width: 8px;height: 8px;border-radius: 50%;background-color:${
            point.series.color
          }; color: ${
            point.series.color
          }; margin-right: 6px"></div><p class="body-s-regular">${
            point.series.name
          }</p> <div style="margin: 0 4px">-</div><p class="body-s-regular">${point.y
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₽</p></div>`
        );
      }, '');
      return (
        allData + // @ts-ignore
        `<p class="body-s-regular" style="color: #86888F">${this.x}</p>`
      );
    },
    shared: true,
    backgroundColor: 'transparent',
    borderColor: 'none',
    borderRadius: 0,
    borderWidth: 0,
    style: {
      fontWeight: '450',
      fontSize: '14px',
      lineHeight: '20px',
      whiteSpace: '30px',
    },
  },
  legend: {
    align: 'left',
    itemMarginTop: 40,
    navigation: {
      activeColor: '#003399',
      animation: true,
      arrowSize: 1,
      enabled: true,
      inactiveColor: '#cccccc',
    },
    itemStyle: {
      fontWeight: '450',
      fontSize: '14px',
      lineHeight: '20px',
    },
    symbolHeight: 0,
    symbolWidth: 0,
    symbolPadding: 0,
    itemDistance: 0,
    labelFormatter() {
      // @ts-ignore
      let nameLegends = this.name;
      if (props.isCollapsed) {
        switch (nameLegends) {
          case 'Максимальная':
            nameLegends = 'Макс. &nbsp;&nbsp;&nbsp;&nbsp;';
            break;
          case 'Минимальная':
            nameLegends = 'Мин. &nbsp;&nbsp;&nbsp;&nbsp;';
            break;
          default:
            // @ts-ignore
            nameLegends = `${this.name} &nbsp;&nbsp;&nbsp;&nbsp;`;
        }
      }
      // @ts-ignore
      const color: string = this.color;
      const display: string = nameLegends ? 'flex' : 'none';
      const item: string = `
        <div class="body-s-regular" style="display: ${display}; gap: 8px; align-items: center;">
          <div style="background:${color}; width: 6px;height: 12px;border-radius:6px;"></div>
          ${nameLegends}
        </div>`;
      return item;
    },
  },
  series: [],
};

const setTimePeriod = (period: string) => {
  if (!period) return;
  showTime.value = period;
  getDataAxios();
  mobileSize(true);
};

const setColorChartLine = (items: Item[]) => {
  const fillColorSetting = {
    linearGradient: [0, 0, 0, 270],
    stops: [
      [0, 'rgba(227, 227, 227, 0.12)'],
      [1, 'rgba(125, 151, 255, 0.12)'],
      [2, 'rgba(168, 186, 245, 0.12)'],
    ],
  };
  const fakeFillColorSetting = {
    linearGradient: [0, 0, 0, 0],
    stops: [[0, 'rgba(227, 227, 227, 0)']],
  };

  return items?.map((item) => {
    if (item?.name === 'Максимальная')
      return Object.assign(
        item,
        { color: '#efae64' },
        { fillColor: fillColorSetting },
        { type: 'areaspline' }
      );
    if (item.name === 'Средняя')
      return Object.assign(
        item,
        { color: '#2D56FF' },
        { fillColor: fakeFillColorSetting },
        { type: 'areaspline' }
      );
    if (item.name === 'Минимальная')
      return Object.assign(
        item,
        { color: '#1EC923' },
        { fillColor: fakeFillColorSetting },
        { type: 'areaspline' }
      );
    return {};
  });
};

const getDataAxios = () => {
  if ('series' in chartOptions && axiosData.value) {
    if (showTime.value === 'daily') {
      chartOptions.series = setColorChartLine(axiosData.value.daily?.series);
      chartOptions.xAxis.categories = axiosData.value.daily?.categories;
      chartOptions.xAxis.min = 0.1;
      chartOptions.xAxis.max = axiosData.value.daily.categories?.length - 1.1;
    } else if (showTime.value === 'sixMonth') {
      chartOptions.series = setColorChartLine(
        axiosData?.value?.sixMonth?.series
      );
      chartOptions.xAxis.categories = axiosData?.value?.sixMonth?.categories;
      chartOptions.xAxis.min = 0.44;
      chartOptions.xAxis.max =
        axiosData?.value?.sixMonth?.categories?.length - 1.5;
    }
  }
};

const mobileSize = (data: boolean) => {
  if (!data) return;
  const monthes: { [index: string]: string } = {
    '01': 'янв',
    '02': 'фев',
    '03': 'мар',
    '04': 'апр',
    '05': 'май',
    '06': 'июн',
    '07': 'июл',
    '08': 'авг',
    '09': 'сен',
    '10': 'окт',
    '11': 'ноя',
    '12': 'дек',
  };
  const points =
    showTime.value === 'sixMonth'
      ? axiosData?.value?.sixMonth?.categories?.length
      : axiosData?.value?.daily?.categories?.length;
  if (isMobile.value) {
    chartOptions.tooltip.style.fontSize = '12px';
    chartOptions.tooltip.style.lineHeight = '16px';
    chartOptions.xAxis.labels.style.fontSize = '12px';
    chartOptions.xAxis.labels.style.lineHeight = '16px';
    chartOptions.yAxis.labels.style.fontSize = '12px';
    chartOptions.yAxis.labels.style.lineHeight = '16px';
    chartOptions.legend.itemStyle.fontSize = '12px';
    chartOptions.legend.itemStyle.lineHeight = '16px';
    chartOptions.legend.symbolPadding = 4;
    chartOptions.legend.itemDistance = 8;
    chartOptions.legend.itemMarginTop = 40;
  } else {
    chartOptions.tooltip.style.fontSize = '14px';
    chartOptions.tooltip.style.lineHeight = '20px';
    chartOptions.xAxis.labels.style.fontSize = '14px';
    chartOptions.xAxis.labels.style.lineHeight = '20px';
    chartOptions.yAxis.labels.style.fontSize = '14px';
    chartOptions.yAxis.labels.style.lineHeight = '20px';
    chartOptions.legend.itemStyle.fontSize = '14px';
    chartOptions.legend.itemStyle.lineHeight = '20px';
    chartOptions.legend.symbolPadding = 8;
    chartOptions.legend.itemDistance = 8;
    chartOptions.legend.itemMarginTop = 40;
  }

  if (props.isCollapsed) {
    chartOptions.yAxis.visible = false;
    chartOptions.chart.spacingLeft = -5;
    chartOptions.chart.marginTop = 22;
    chartOptions.chart.spacingTop = 15;
    chartOptions.chart.spacingRight = -7;
    chartOptions.title.align = 'left';
    chartOptions.title.x = 20;
    chartOptions.title.useHTML = false;
    chartOptions.xAxis.showFirstLabel = false;
    chartOptions.xAxis.showLastLabel = false;
    chartOptions.xAxis.labels.rotation = 0;
    chartOptions.xAxis.labels.align = 'center';
    chartOptions.xAxis.labels.x = 0;
    chartOptions.xAxis.visible = true;
    chartOptions.legend.align = 'center';
    chartOptions.legend.itemMarginTop = 16;
    chartOptions.legend.useHTML = true;
    chartOptions.tooltip.followPointer = true;
    if (isShowCollapsedSixMonth.value && isShowCollapsedSixMonth.value >= 3) {
      chartOptions.xAxis.labels.formatter = (value) => {
        if (value && value?.value)
          return monthes[value.value.replace(/^\d\d\./, '')];
        else return monthes[value.replace(/^\d\d\./, '')];
      };
      chartOptions.xAxis.tickInterval = 4;
    } else {
      chartOptions.xAxis.labels.formatter = undefined;
      if (points === 7 || points === 8) {
        chartOptions.xAxis.showFirstLabel = true;
        chartOptions.xAxis.showLastLabel = true;
        chartOptions.xAxis.tickInterval = 2;
        chartOptions.xAxis.labels.x = 15;
      } else if (points <= 5) {
        chartOptions.xAxis.labels.x = 30;
        chartOptions.xAxis.tickInterval = 1;
      } else {
        chartOptions.xAxis.tickInterval = Math.round(points / 6);
      }
    }
  } else {
    chartOptions.yAxis.visible = true;
    chartOptions.chart.marginTop = undefined;
    chartOptions.chart.spacingLeft = 10;
    chartOptions.chart.spacingTop = 10;
    chartOptions.chart.spacingRight = 10;
    chartOptions.title.text = '';
    chartOptions.title.align = 'center';
    chartOptions.title.x = 0;
    chartOptions.title.useHTML = false;
    chartOptions.xAxis.showFirstLabel = false;
    chartOptions.xAxis.showLastLabel = false;
    chartOptions.xAxis.labels.rotation = undefined;
    chartOptions.xAxis.tickInterval = undefined;
    chartOptions.xAxis.labels.align = 'center';
    chartOptions.xAxis.labels.x = 0;
    chartOptions.xAxis.labels.formatter = undefined;
    chartOptions.xAxis.visible = true;
    chartOptions.xAxis.tickInterval = 1;
    if (isLg.value && points > 20) {
      chartOptions.xAxis.tickInterval = 2;
    }
    chartOptions.legend.align = 'left';
    chartOptions.legend.itemMarginTop = 40;
    chartOptions.legend.useHTML = true;
    chartOptions.tooltip.followPointer = false;
  }
};

const onResize = useDebounceFn(() => {
  reDrawChart.value = false;
  setTimeout(() => {
    reDrawChart.value = true;
  }, 250);
}, 50);

watch(
  () => width.value,
  () => {
    onResize();
  }
);

watch(
  () => isMobile.value,
  () => {
    mobileSize(true);
    // reRender
    const old = showTime.value;
    showTime.value = 'false';
    setTimeout(() => {
      showTime.value = old;
    }, 100);
  }
);

watch(
  () => axiosData.value,
  () => {
    if (
      !axiosData?.value?.sixMonth ||
      axiosData?.value?.sixMonth?.categories?.length <= 5
    ) {
      setTimePeriod('daily');
    } else if (axiosData?.value?.sixMonth?.categories && props.isCollapsed) {
      setTimePeriod('sixMonth');
    }
  }
);
watch(
  () => props.isCollapsed,
  () => {
    if (
      props.isCollapsed &&
      axiosData?.value?.sixMonth &&
      axiosData?.value?.sixMonth?.categories?.length > 5
    ) {
      setTimePeriod('sixMonth');
    } else {
      setTimePeriod('daily');
    }
  }
);

onBeforeMount(async () => {
  if (!import.meta.env.SSR && props?.pageData?.model) {
    if ('priceChart' in props?.pageData) return;
    await API.get(`/models/${props?.pageData?.model.id}/history`, {
      params: {
        region_id: globalStore.region.id,
        device: globalStore.device || 1,
      },
    })
      .then((res) => {
        if (!res.data) {
          emit('componentHide', 'price-chart');
          return [];
        }
        axiosData.value = res.data;
        emit('change-data', {
          key: 'priceChart',
          data: res.data,
        });
        getDataAxios();
      })
      .catch(() => {
        emit('componentHide', 'price-chart');
      });
  }
});

onMounted(() => {
  getDataAxios();
  mobileSize(true);
  setTimeout(() => {
    reDrawChart.value = true;
  }, 50);
});
</script>
<style scoped lang="scss" src="./PriceChartScss.scss" />
